import * as React from 'react';
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { ENV_CONFIG } from 'config/env';
import { ButtonSortChart, CHART_SORT_OPTIONS } from './ButtonSortChart';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';

interface BarChartProps {
  data?: {
    [rowId: string]: {
      [colId: string]: string | number;
    };
  };
  rowDimension?: AutoCompleteItem;
  columnDimension?: AutoCompleteItem;
  dataDimension?: AutoCompleteItem;
  scaleLabel?: string;
  totalData?: number;
}

export interface TableAnalyticBarChartRef {
  downloadChart: () => void;
}

const CHART_GROUPING_VALUE = {
  COLUMN: 'column',
  ROW: 'row',
};

const toggleButtonStyles = {
  fontSize: '12px',
  textTransform: 'none' as const,
  fontWeight: 600,
  px: 2,
  py: 0.5,
  borderColor: '#e5e7eb',
  color: '#667085',
  '&.Mui-selected': {
    backgroundColor: '#ecfdf5',
    color: '#42BB93',
    borderColor: '#42BB93',
    '&:hover': {
      backgroundColor: '#d1fae5',
    },
  },
  '&:hover': {
    backgroundColor: '#f9fafb',
  },
};

function convertDataRowToChartData(
  data: { [colId: string]: string | number },
  legendLabel?: string,
  scaleLabel?: string,
  totalData?: number,
  sortType?: string
) {
  let legendLabelChart = legendLabel || 'Total';
  if (legendLabelChart !== 'Total' && scaleLabel) {
    legendLabelChart = `${legendLabelChart} (${scaleLabel})`;
  }
  if (totalData) {
    legendLabelChart = `${legendLabelChart} : ${totalData?.toLocaleString()}`;
  }

  let result = [['category', legendLabelChart]];

  for (let key in data) {
    if (key !== 'Total' && key !== 'Total %') {
      result.push([key, data[key]] as string[]);
    }
  }

  if (CHART_SORT_OPTIONS.ASC === sortType) {
    // @ts-ignore
    result.sort((a, b) => a[1] - b[1]);
  }
  if (CHART_SORT_OPTIONS.DESC === sortType) {
    // @ts-ignore
    result.sort((a, b) => b[1] - a[1]);
  }
  return result;
}

function convertDataColumnToChartData(
  rawData: {
    [rowId: string]: {
      [colId: string]: string | number;
    };
  },
  legendLabel?: string,
  scaleLabel?: string,
  totalData?: number,
  sortType?: string
) {
  let legendLabelChart = legendLabel || 'Total';
  if (legendLabelChart !== 'Total' && scaleLabel) {
    legendLabelChart = `${legendLabelChart} (${scaleLabel})`;
  }
  if (totalData) {
    legendLabelChart = `${legendLabelChart} : ${totalData?.toLocaleString()}`;
  }
  let result = [['category', legendLabelChart]];

  for (let key in rawData) {
    if (
      rawData[key] &&
      typeof rawData[key] === 'object' &&
      'Total' in rawData[key] &&
      key !== 'Total' &&
      key !== 'Total %'
    ) {
      result.push([key, rawData[key]['Total'] as number] as string[]);
    }
  }

  if (CHART_SORT_OPTIONS.ASC === sortType) {
    // @ts-ignore
    result.sort((a, b) => a[1] - b[1]);
  }
  if (CHART_SORT_OPTIONS.DESC === sortType) {
    // @ts-ignore
    result.sort((a, b) => b[1] - a[1]);
  }

  return result;
}

export const ChartTableAnalytic = React.forwardRef<TableAnalyticBarChartRef, BarChartProps>(
  (props: BarChartProps, ref) => {
    const { data, dataDimension, scaleLabel, totalData } = props;
    const chartRef = React.useRef(null);
    const [selectedChartGrouping, setSelectedChartGrouping] = React.useState(CHART_GROUPING_VALUE.COLUMN);
    const [chartInstance, setChartInstance] = React.useState<any>(null);
    const [selectedSortChart, setSelectedSortChart] = React.useState(CHART_SORT_OPTIONS.NONE);

    const chartData = React.useMemo(() => {
      if (!data) return [];
      if (selectedChartGrouping === CHART_GROUPING_VALUE.COLUMN) {
        return convertDataColumnToChartData(data, dataDimension?.label, scaleLabel, totalData, selectedSortChart);
      }
      return convertDataRowToChartData(data?.['Total'], dataDimension?.label, scaleLabel, totalData, selectedSortChart);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, selectedChartGrouping, selectedSortChart]);

    const hasData = chartData.length > 1;

    React.useEffect(() => {
      const loadECharts = () => {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = `${ENV_CONFIG.endpoint.RUNTIME_ENDPOINT}images/echarts.min.js`;
          script.onload = resolve;
          script.onerror = reject;
          document.body.appendChild(script);
        });
      };

      const initChart = () => {
        if (chartData.length === 0) {
          return;
        }
        if (!chartRef.current) {
          console.error('Chart container not found');
          return;
        }
        //@ts-ignore
        if (typeof window === 'undefined' || !window.echarts) {
          console.error('ECharts is not available');
          return;
        }

        //@ts-ignore
        const instance = window.echarts.init(chartRef.current);
        setChartInstance(instance);

        const option = {
          legend: {},
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' },
          },
          dataset: {
            source: chartData,
          },
          xAxis: {
            type: 'category',
            axisLabel: {
              interval: 0,
              rotate: 30,
              formatter: function (value: any) {
                const maxLength = 15;
                if (value.length > maxLength) {
                  return value.substring(0, maxLength) + '...';
                }
                return value;
              },
            },
          },
          yAxis: {},
          series: [
            {
              type: 'bar',
              itemStyle: {
                color: '#42BB93',
                borderRadius: [4, 4, 0, 0],
              },
              emphasis: {
                itemStyle: {
                  color: '#37a07e',
                },
              },
              label: {
                normal: {
                  show: true,
                  position: 'top',
                  fontWeight: 'bold',
                  fontSize: 11,
                  formatter: function (params: any) {
                    return params.value[1].toLocaleString();
                  },
                },
              },
            },
          ],
          grid: {
            left: '3%',
            right: '4%',
            bottom: '12%',
            containLabel: true,
          },
        };

        instance.setOption(option);
      };

      loadECharts()
        .then(initChart)
        .catch((error) => console.error('Failed to load ECharts:', error));

      return () => {
        if (chartInstance) {
          chartInstance.dispose();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartData]);

    const handleDownload = () => {
      if (chartInstance) {
        const dataURL = chartInstance.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        });

        const link = document.createElement('a');
        link.href = dataURL;
        link.download = 'chart-image.png';
        link.click();
      }
    };

    React.useImperativeHandle(ref, () => ({
      downloadChart: handleDownload,
    }));

    return (
      <Stack mt={1.5} gap={1.5}>
        {/* Controls Row */}
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <ToggleButtonGroup
            value={selectedChartGrouping}
            exclusive
            onChange={(_event, value) => {
              if (value !== null) setSelectedChartGrouping(value);
            }}
            size='small'
            sx={{ height: 32 }}
          >
            <ToggleButton value={CHART_GROUPING_VALUE.COLUMN} sx={toggleButtonStyles}>
              {props.columnDimension?.label || 'Column'}
            </ToggleButton>
            <ToggleButton value={CHART_GROUPING_VALUE.ROW} sx={toggleButtonStyles}>
              {props.rowDimension?.label || 'Row'}
            </ToggleButton>
          </ToggleButtonGroup>
          <ButtonSortChart
            onChange={(value) => {
              setSelectedSortChart(value);
            }}
          />
        </Stack>

        {/* Chart Container */}
        <Stack
          sx={{
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            backgroundColor: '#fff',
            overflow: 'hidden',
          }}
        >
          {hasData ? (
            <div ref={chartRef} style={{ width: '100%', height: 420, padding: '12px 8px 6px' }} />
          ) : (
            <Stack alignItems='center' justifyContent='center' height={420} gap={1.5}>
              <TroubleshootIcon sx={{ fontSize: 48, color: '#d0d5dd' }} />
              <Typography sx={{ fontSize: '14px', color: '#98A2AE', fontWeight: 500 }}>
                No chart data available
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#b0b8c4' }}>
                Adjust your parameters above to generate a chart
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    );
  }
);
